// src/components/home/faq.js
//
// Pure helpers for the FAQ (home-packfinder). Shared by the home page, the /faq page
// AND scripts/prerender.mjs, so the questions a crawler reads in dist/faq/index.html
// and its FAQPage structured data are the same items the live page shows -- one
// resolver, no drift. Plain JS on purpose: the prerender script imports it under Node.
import { boardFaq } from './marketingCopy.js';
import { DEFAULT_FAQ } from '../../config/siteText.js';
import { updateLegacyFaqAnswer } from './offerCopy.js';
import { updateLegacyPaymentAnswer } from '../../config/paymentCopy.js';

// The /faq page's title + meta description: ONE copy, read by FaqPage.jsx (client-side
// title swap) and by prerender.mjs (the baked <title> / description / og tags).
export const FAQ_SEO = {
    title: 'Common Questions — Between the Lines with Danielle',
    description: 'IGCSE English study pack FAQs: Edexcel, OxfordAQA, Cambridge (CIE) coming soon, free lessons, access, payment and AI essay feedback.',
};

const str = (v) => (typeof v === 'string' ? v : '');
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

// Site Text -> { enabled, heading, items, visible }.
//
// Same rule as resolvePackFinder: state.siteText on the live site is the SAVED doc, so
// an ABSENT `faq` block means "the shipped FAQ", while a block Danielle has saved is
// authoritative -- including an empty `items` list, which is a way to switch it off.
// An item needs BOTH a question and an answer; a half-filled row is not shown (a
// question with no answer is worse than no question).
export function resolveFaq(siteText) {
    const st = isObj(siteText) ? siteText : {};
    const raw = isObj(st.faq) ? st.faq : {};
    const source = Array.isArray(raw.items) ? raw.items : DEFAULT_FAQ.items;
    const items = source
        .filter(isObj)
        .map((it) => ({ q: str(it.q).trim(), a: updateLegacyFaqAnswer(str(it.a).trim()) }))
        .filter((it) => it.q && it.a)
        .map(boardFaq)
        .map(it => ({ ...it, a: updateLegacyPaymentAnswer(it.a) }));
    const enabled = raw.enabled !== false;
    return {
        enabled,
        heading: str(raw.heading).trim() || DEFAULT_FAQ.heading,
        items,
        visible: enabled && items.length > 0,
    };
}

// schema.org FAQPage for the prerendered /faq page. Returns null for an empty list --
// an FAQPage with no questions is invalid markup, and the caller then emits none.
// The text is the visible answer verbatim: structured data must match what is on the
// page. (Escaping for the <script> element is applyMeta's job in prerender.mjs.)
export function faqJsonLd(items) {
    const list = (Array.isArray(items) ? items : []).filter((it) => isObj(it) && str(it.q).trim() && str(it.a).trim());
    if (!list.length) return null;
    return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: list.map((it) => ({
            '@type': 'Question',
            name: str(it.q).trim(),
            acceptedAnswer: { '@type': 'Answer', text: str(it.a).trim() },
        })),
    };
}
